import { MouseEvent, ChangeEvent, FormEvent, useState } from "react";

import { toDateString } from "~/libs";

import {
  useInput,
  useArray,
  useApisManagersRoleplayingConditionsIndex,
  useDateRange,
  useProvidersEmployeeLayout,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
  useMultipleDropdown,
  useApisManagersRoleplayingConditionSearchItemsIndex,
  useApisManagersBelongingEmployeesRoleplayingConditionsIndex,
} from "~/hooks";

import {
  ApiManagersRoleplayingConditionsIndexRequestType,
  BusinessDivisionType,
  ChildType,
  RoleplayingConditionType,
  ParentType,
  RangeDatePropsType,
  MultiValueType,
  OptionType,
} from "~/domains";

type ReturnType = {
  roleplayingConditions: RoleplayingConditionType[];
  hasNextPage?: boolean;
  keyword: string;
  isLoading: boolean;
  startDate: Date | null;
  endDate: Date | null;
  selectableDropdownCreatedEmployees: OptionType[];
  selectedDropdownCreatedEmployees: MultiValueType<OptionType>;
  selectableDropdownParticipationEmployees: OptionType[];
  selectedDropdownParticipationEmployees: MultiValueType<OptionType>;
  selectableDropdownDivisions: BusinessDivisionType[];
  selectedDropdownDivisions: BusinessDivisionType[];
  selectableDropdownSections: ChildType[];
  selectedDropdownSections: ChildType[];
  onDivisionChange: (value: ParentType[]) => void;
  onSectionChange: (value: ChildType[]) => void;
  onChangeDropdownCreatedEmployees: (value: MultiValueType<OptionType>) => void;
  onChangeDropdownParticipationEmployees: (
    value: MultiValueType<OptionType>,
  ) => void;
  onChangeDateRange: (value: RangeDatePropsType) => void;
  deleteIndexItem: (index: number) => void;
  unshiftItem: (item: RoleplayingConditionType) => void;
  updateIndexItem: (item: RoleplayingConditionType, index: number) => void;
  findAndRemove: (func: (item: RoleplayingConditionType) => boolean) => void;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  fetchNextPage: () => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
};

type PropsTYpe = {
  useApisRoleplayingConditions:
    | typeof useApisManagersRoleplayingConditionsIndex
    | typeof useApisManagersBelongingEmployeesRoleplayingConditionsIndex;
};

export const useSearchManagersRoleplayingConditions = ({
  useApisRoleplayingConditions,
}: PropsTYpe): ReturnType => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const { setFalseRightSidebarMain } = useProvidersEmployeeLayout();
  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");
  const [{ startDate, endDate, onChange: onChangeDateRange }] = useDateRange();
  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  const [
    {
      dropdownValue: selectedDropdownCreatedEmployees,
      onChange: onChangeDropdownCreatedEmployees,
      dropdownSelectableValue: selectableDropdownCreatedEmployees,
      setSelectableOption: setSelectableDropdownCreatedEmployees,
    },
    setDropdownCreatedEmployees,
  ] = useMultipleDropdown();

  const [
    {
      dropdownValue: selectedDropdownParticipationEmployees,
      onChange: onChangeDropdownParticipationEmployees,
      dropdownSelectableValue: selectableDropdownParticipationEmployees,
      setSelectableOption: setSelectableDropdownParticipationEmployees,
    },
    setDropdownParticipationEmployees,
  ] = useMultipleDropdown();

  const [params, setParams] =
    useState<ApiManagersRoleplayingConditionsIndexRequestType>();

  const {
    items: roleplayingConditions,
    setItems: setRoleplayingConditions,
    deleteIndexItem,
    unshiftItem,
    updateIndexItem,
    findAndRemove,
  } = useArray<RoleplayingConditionType>([]);

  useApisManagersRoleplayingConditionSearchItemsIndex({
    config: {
      onSuccess: (res) => {
        setSelectableDropdownCreatedEmployees(res.employees);
        setSelectableDropdownParticipationEmployees(res.employees);
      },
    },
  });
  const { fetchNextPage, hasNextPage, isFetching } =
    useApisRoleplayingConditions({
      params,
      config: {
        onSuccess: (data) => {
          setRoleplayingConditions(
            data.pages.map((page) => page.roleplayingConditions).flat(),
          );
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
      postDateFrom: startDate ? toDateString(startDate) : "",
      postDateTo: endDate ? toDateString(endDate) : "",
      businessDivisionIds: selectedDivisions.map((division) => division.id),
      businessSectionIds: optionSelectedSections.map((section) => section.id),
      createdEmployeeIds: selectedDropdownCreatedEmployees.map(
        (employee) => employee.value,
      ),
      participationEmployeeIds: selectedDropdownParticipationEmployees.map(
        (employee) => employee.value,
      ),
    });
    setFalseRightSidebarMain();
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setKeyword("");
    onChangeDateRange([null, null]);
    setParams(undefined);
    setFalseRightSidebarMain();
    setDropdownCreatedEmployees([]);
    setDropdownParticipationEmployees([]);
  };

  return {
    roleplayingConditions,
    hasNextPage,
    isLoading: isFetching,
    startDate,
    endDate,
    keyword,
    selectableDropdownCreatedEmployees,
    selectedDropdownCreatedEmployees,
    selectableDropdownParticipationEmployees,
    selectedDropdownParticipationEmployees,
    selectableDropdownDivisions: selectableDivisions,
    selectedDropdownDivisions: selectedDivisions,
    selectableDropdownSections: optionSelectableSections,
    selectedDropdownSections: optionSelectedSections,
    onDivisionChange,
    onSectionChange,
    onChangeDropdownCreatedEmployees,
    onChangeDropdownParticipationEmployees,
    fetchNextPage,
    onChangeDateRange,
    unshiftItem,
    updateIndexItem,
    deleteIndexItem,
    findAndRemove,
    onChangeKeyword,
    onConditionReset,
    onSearchSubmit,
  };
};
